import React, { useState, useRef } from 'react';
import { Search, Sparkles, Loader2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSubscription } from '../../hooks/useSubscription';
import { useResourceSeries } from '../../hooks/useResourceSeries';
import { SeriesCard } from './SeriesCard';
import { SeriesFilterSidebar } from './SeriesFilterSidebar';
import { SeriesUpgradeGate } from './SeriesUpgradeGate';

interface AISearchResult {
  books: string[];
  topics: string[];
  keywords: string[];
}

export function SeriesLibraryPage() {
  const { hasSeriesAccess } = useSubscription();
  const { series, loading, error, filters, setFilters, availableTags, availableBooks } = useResourceSeries();

  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!hasSeriesAccess) {
    return <SeriesUpgradeGate />;
  }

  const runAISearch = async () => {
    const query = aiQuery.trim();
    if (!query) return;
    setAiLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ type: 'seriessearch', prompt: query }),
      });

      if (!response.ok) throw new Error('AI search failed');
      const result = await response.json();

      let parsed: AISearchResult = { books: [], topics: [], keywords: [] };
      try {
        parsed = JSON.parse(result.content);
      } catch {
        // If parse fails, fall back to keyword search
        parsed.keywords = query.split(/\s+/).filter(w => w.length > 2);
      }

      setFilters({
        books: parsed.books ?? [],
        topics: parsed.topics ?? [],
        keyword: parsed.keywords?.join(' ') ?? '',
      });
      setAiApplied(true);
    } catch {
      // Fall back to plain keyword search
      setFilters({ keyword: query });
      setAiApplied(true);
    } finally {
      setAiLoading(false);
    }
  };

  const clearAISearch = () => {
    setAiQuery('');
    setAiApplied(false);
    setFilters({ books: [], topics: [], keyword: '' });
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') runAISearch();
  };

  const activeFilterCount = filters.books.length + filters.topics.length + (filters.keyword ? 1 : 0);

  return (
    <div className="flex flex-col min-h-full">

      {/* Header */}
      <div className="px-6 pt-6 pb-5 border-b border-sand/20 dark:border-slate/20">
        <h1 className="text-xl font-bold text-midnight dark:text-soft-white mb-1">Sermon Series Library</h1>
        <p className="text-sm text-slate dark:text-soft-white/50 mb-4">
          Pre-packaged series with notes, graphics, slides, and social art — ready to import.
        </p>

        {/* AI Search bar */}
        <div className="relative max-w-2xl">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all bg-white dark:bg-midnight/50 ${
            aiApplied
              ? 'border-electric shadow-[0_0_0_3px_rgba(99,102,241,0.1)]'
              : 'border-sand/30 dark:border-slate/30 focus-within:border-electric focus-within:shadow-[0_0_0_3px_rgba(99,102,241,0.1)]'
          }`}>
            {aiLoading
              ? <Loader2 className="h-4 w-4 animate-spin text-electric shrink-0" />
              : <Sparkles className={`h-4 w-4 shrink-0 ${aiApplied ? 'text-electric' : 'text-slate/40'}`} />
            }
            <input
              ref={inputRef}
              value={aiQuery}
              onChange={e => { setAiQuery(e.target.value); if (aiApplied) setAiApplied(false); }}
              onKeyDown={handleKeyDown}
              placeholder="What do you want to preach about?"
              className="flex-1 text-sm bg-transparent text-midnight dark:text-soft-white placeholder:text-slate/40 focus:outline-none"
            />
            {aiQuery && (
              <button onClick={clearAISearch} className="p-0.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 shrink-0">
                <X className="h-3.5 w-3.5 text-slate/50" />
              </button>
            )}
            <button
              onClick={runAISearch}
              disabled={!aiQuery.trim() || aiLoading}
              className="shrink-0 px-3 py-1.5 bg-electric text-white text-xs font-medium rounded-lg hover:bg-electric/90 disabled:opacity-40 transition-colors"
            >
              Search
            </button>
          </div>
          {aiApplied && activeFilterCount > 0 && (
            <p className="mt-2 text-xs text-electric flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              AI found {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} — showing matching series
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex gap-6 p-4 pb-24 md:p-6 md:pb-12">
        <div className="hidden md:block">
          <SeriesFilterSidebar filters={filters} onChange={setFilters} availableTags={availableTags} availableBooks={availableBooks} />
        </div>

        <div className="flex-1 min-w-0">
          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate/40" />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-4 py-3 rounded-lg">{error}</p>
          )}

          {!loading && !error && series.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
              <Search className="h-8 w-8 text-slate/20" />
              <p className="text-slate dark:text-soft-white/50 text-sm">
                {activeFilterCount > 0
                  ? 'No series match your filters. Try adjusting or clearing them.'
                  : 'No series published yet. Check back soon.'}
              </p>
              {activeFilterCount > 0 && (
                <button onClick={clearAISearch} className="text-sm text-electric hover:underline">
                  Clear filters
                </button>
              )}
            </div>
          )}

          {!loading && series.length > 0 && (
            <>
              <p className="text-xs text-slate dark:text-soft-white/40 mb-3">
                {series.length} series{activeFilterCount > 0 ? ' found' : ''}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {series.map(s => (
                  <SeriesCard
                    key={s.id}
                    series={s}
                    isLocked={false}
                    onUpgradeClick={() => {}}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
